

import { useState, useEffect, useMemo, useRef } from "react";
import { useTheme, useNotify, usePlan } from "./App";
import { supabase, submitSupportTicket } from "./supabase";

const DEFAULT_ARTICLES = [

  // ── GETTING STARTED ────────────────────────────────────────────────────────
  {
    id: "gs-welcome",
    category: "Getting Started",
    categoryIcon: "👋",
    title: "Welcome to VendorStax",
    keywords: ["start", "welcome", "overview", "what is", "intro"],
    content: `VendorStax is a tool built for people who buy and sell trading cards - especially at shows and events.

It helps you keep track of your cards, know what they're worth, and run your table smoothly during a show.

Here's what you can do with VendorStax:

• **Keep track of your cards** - Add every card you own, with its price, condition, and where it came from.
• **Manage your shows** - When you're at a card show, use Show Mode to sell, buy, and trade cards fast.
• **Look up prices** - See what cards are selling for, so you know if you're getting a good deal.
• **Read your sales reports** - Find out which cards are making you money and which ones aren't.
• **Share your inventory with customers** - Your Kiosk lets customers browse your cards on a tablet at your table.


To get started, add your first card by going to **Inventory** and tapping the **+ Add Card** button.`,
  },

  {
    id: "gs-plans",
    category: "Getting Started",
    categoryIcon: "👋",
    title: "Free vs Pro Plan",
    keywords: ["free", "pro", "paid", "plan", "upgrade", "limit", "features"],
    content: `VendorStax has two plans: **Free** and **Pro**.

## Free Plan
The Free plan is great for getting started. Here's what you get:
• Up to **100 cards** in your inventory
• **Show Mode** - sell and buy cards at shows
• **Lots** - group your cards into collections
• **Import from Collectr** - move your data over quickly, capped by your 100-card limit
• **Sales reports** - see what you've sold
• A **Business Name**, logo, and Shop URL you can always edit, no matter your plan

## Pro Plan
The Pro plan unlocks everything. Here's what's extra:
• **Unlimited cards** in your inventory
• **Trade tab** in Show Mode - evaluate trades in real time
• **Consignors**, plus your Consignment Settings (commission rate, minimum card value)
• **Buyers List** - keep track of customers who want specific cards
• **Market Pricing** - see live price data for your cards
• **Kiosk Mode** for shows, and **Store Mode** for a physical shop that's always on
• **Team Members** and the **Audit Log**
• Setting your own **Trade Credit Rate**, **Cash Buy Rate**, **Minimum Sell Price Markup**, and **Breakeven Buffer**
• **Tax Tracking** on your sales
• **Advanced Reports** - deeper data about your business

## What You Can Still See on Free
Settings shows you every Pro feature, even on Free, so you know what's there. Locked sections show a short preview instead of the real controls, with a link to upgrade right where you'd need it.

## Price Setting Is Its Own Add-On
Automatic pricing (Price Setting) isn't included with Pro automatically. It's a separate **$9 a month** add-on you turn on from Settings once you're already on Pro.

To upgrade, tap **⭐ Upgrade to Pro** at the bottom of the sidebar, it's always there on the Free plan. It opens a real checkout right in the app, no need to contact anyone or wait on a reply.
`,
  },

  {
    id: "gs-upgrade-prompts",
    category: "Getting Started",
    categoryIcon: "👋",
    title: "Upgrade Prompts and Emails You Might See",
    keywords: ["upgrade prompt", "email", "notification", "100 cards", "nearing limit", "price changes"],
    content: `On the Free plan, VendorStax occasionally nudges you toward Pro at moments where it's actually relevant, never randomly. Here's what you might see and why.

## The Upgrade Button
A small **⭐ Upgrade to Pro** button sits at the bottom of the sidebar the whole time you're on Free. Tap it anytime, it opens the real checkout directly, no forms, no waiting.

## Your First Show
The first time you create a show on the Free plan, a short prompt appears right after it saves, pointing out what Pro adds for Show Mode. It only shows up once, the first time, not on every show after that.

## "You're Rocking It!" Email
If you're getting close to the 100-card limit on Free, you'll get a friendly email letting you know, along with how many cards you've added so far. It's not a warning, just a heads-up before you hit the ceiling, so it doesn't catch you off guard mid-show.

## Price Change Email Before a Show
If you manually change prices on a lot of cards, more than 20, in the week leading up to an upcoming show, you'll get an email pointing out that Price Setting (the automatic pricing add-on) could handle that for you going forward. This only fires once per show, not every time you touch a price.

None of these block anything you're doing, they're just timed to show up when they're actually useful.`,
  },

  {
    id: "gs-account",
    category: "Getting Started",
    categoryIcon: "👋",
    title: "Your Account and Settings",
    keywords: ["account", "settings", "business name", "logo", "rates", "sign in", "google"],
    content: `You sign in to VendorStax with your **Google account**. There's no separate password to remember.

## Setting Up Your Business
Go to **Settings** to set up your business:

1. **Business Name** - This shows up on your Kiosk and receipts. Use the name your customers know you by.
2. **Logo** - Upload your shop's logo (PNG, JPG, or SVG, max 2MB). It shows on your Kiosk screen.
3. **Rates** - These numbers affect how the app calculates prices for you:
   - **Cash Buy Rate** - What percent of a card's value you pay in cash when buying (e.g. 70%)
   - **Trade Credit Rate** - What percent you offer as store credit (e.g. 80%)
   - **Markup Rate** - How much above cost you price your cards (e.g. 125%)
   - **Breakeven Buffer** - A small cushion added to make sure you don't lose money


## Shared Access
If someone else (like a business partner) needs to access your account, they can be added as a **shared email**. They'll see your same inventory and data. Ask your VendorStax admin to set this up.`,
  },

  // ── INVENTORY ──────────────────────────────────────────────────────────────
  {
    id: "inv-overview",
    category: "Inventory",
    categoryIcon: "📦",
    title: "Understanding Your Inventory",
    keywords: ["inventory", "cards", "stock", "collection", "in stock", "reserved", "sold"],
    content: `Your **Inventory** is the list of all the cards you own (or used to own).

Every card has a **status** that tells you where it is:
- **In Stock** - You have the card and it's available to sell.
- **Reserved** - The card is added to an active show. It's still yours, but it's at the show table.
- **Sold** - You've sold the card. It stays in your records so you can track your earnings.


## Filtering Your Inventory
You can filter by:
- **Game** - Pokémon or Lorcana
- **Status** - In Stock, Reserved, or Sold
- **Search** - Type any card name or set name to find it fast

## The Card Tile
Each card shows:
- Card name and set
- Condition (Near Mint, Lightly Played, etc.)
- Your **list price** (what you're selling it for)
- The **market price** (what it's going for online - Pro only)

Tap any card to see full details or make changes.`,
  },

  {
    id: "inv-add",
    category: "Inventory",
    categoryIcon: "📦",
    title: "Adding Cards to Your Inventory",
    keywords: ["add card", "new card", "scan", "camera", "search", "manual", "entry"],
    content: `There are three ways to add a card:

## Option 1: Search (Fastest)
Tap **+ Add Card**, then type the card name. The app searches online and shows you a picture grid of results. Tap the right card and most details fill in automatically.


## Option 2: Scan with Camera (Great for batches)
Tap **+ Add Card**, then tap the **📷 camera icon**. Point your phone at the front of the card. The app reads the card name and number, then searches for it automatically.

Tips for good scans:
- Lay the card flat on a dark surface
- Make sure the card name at the top is fully visible
- Good lighting makes a big difference - avoid glare


## Option 3: Manual Entry
Tap **+ Add Card**, then tap **"Skip search - add manually"**. Fill in the card name, set, condition, and price yourself. This works for any card, even ones the search doesn't find.

## What to Fill In
- **Card Name** - The name on the card (e.g. "Charizard ex")
- **Set** - Which set it's from (e.g. "Obsidian Flames")
- **Card #** - The number at the bottom of the card (e.g. "215/197")
- **Condition** - How the card looks (Near Mint is the best)
- **Acquisition Cost** - What you paid for it
- **List Price** - What you're selling it for`,
  },

  {
    id: "inv-custom-items",
    category: "Inventory",
    categoryIcon: "📦",
    title: "Adding Custom Items (Non Trading Card Products)",
    keywords: ["custom item", "handmade", "jewelry", "pins", "binders", "non card", "merch", "resale"],
    content: `If you sell things beyond trading cards, handmade goods, pins, jewelry, binders, and similar items, **Add Custom Item** is a separate, simpler form built for that, right next to the regular Add Card button in Inventory.

## How It's Different From Adding a Card
A custom item skips everything that's specific to cards, no set, no card number, no rarity, no grading. Instead you fill in an item name, category, item type, price, quantity on hand, and a description.

## Category and Item Type
Category is the broad grouping (Jewelry, Pins, Badges & Patches, Apparel, and so on). Item Type is more specific within that (Enamel Pin, Keychain, Tote Bag). Both dropdowns have an **Other / Custom** option at the bottom if nothing on the list fits, just type in your own.

## Handmade vs Resale
Pick **Handmade** for anything you made yourself, and **Resale** for anything you purchased elsewhere to sell. This is just for your own tracking, it doesn't change how the item behaves anywhere else in the app.

## Images and SKU
You can add up to three images per item, either paste a URL or upload a photo directly. SKU is optional, leave it blank and VendorStax generates a short code for you automatically, the same way it does for regular cards.

## Where These Show Up
Custom items use your same **Reports**, and appear in Inventory alongside your cards. They don't have a Reference Price or Dynamic Pricing, since there's no market price source for handmade goods the way there is for trading cards.`,
  },

  {
    id: "inv-pricing",
    category: "Inventory",
    categoryIcon: "📦",
    title: "Reference Price Range",
    keywords: ["reference price", "market price", "market range", "tcgplayer", "pkmnprices", "price", "refresh", "value", "range"],
    content: `VendorStax shows a **Reference Price range** for your cards, not a single exact number.

## Why "Reference," Not "Market"?
We call this a Reference Price rather than a "market price" on purpose. Market price implies a single, objective number - but card prices fluctuate daily and depend on condition, timing, and where you're selling. Reference Price is a starting point for your own pricing decisions, not an absolute you have to match.

## How We Calculate It
The range is built from real recent sold-card data, looking at the last 7 days of actual sales first. If a card hasn't sold enough times in the last week to give a reliable range, we widen the window to 30 days instead. For very low-volume cards with no recent sold data at all, we fall back to an estimated range around the card's listed market price.

The range shown is a reasonable window based on that data, not a guarantee. Always check recent sold listings before making a big buying or selling decision.

## The Reference Range Display
On each card you'll see a range like **$359 – $439** along with the data source it came from (e.g. "7d sold comps") and how fresh the data is.

## Getting a Price for One Card
Tap a card to open it, then tap the **🔄** next to Market Range. The app looks up that specific card right now.

## How the Rest of Your Inventory Gets Priced
New cards are priced automatically the moment you add them - no action needed. There's no "refresh everything" button, since re-pricing a whole large inventory on demand gets expensive fast. Instead, a background job quietly re-checks a batch of cards every night around 3am Eastern, oldest-priced-first, until everything is current. If you're new to this pricing source, expect your existing cards to pick up real data gradually over the next several nights rather than all at once.

**Why does some cards show no range?**
Some cards - especially newer releases, certain Japanese sets, or cards with missing/incorrect details - don't have enough sales data yet, or couldn't be matched. If a card shows "Not fetched yet" instead of a range, that doesn't mean the card is worthless - it means no confident match was found.

## Accurate Card Details Are Required
Fetching a Reference Price depends on the app being able to match your card to the right listing in our price database. If key details are missing or incorrect, the lookup will fail rather than risk pricing the wrong card - it will never guess.

For the best results, make sure these three fields are filled in accurately before fetching a price:

1. **Card Name** - must match exactly as it appears on the card (e.g. "Charizard ex" not "Charizard EX")
2. **Set** - the full set name the card belongs to
3. **Card Number** - the number printed at the bottom of the card (e.g. "215/197" or "GG69")

If the app says it can't fetch a price, open the card and check these fields first. Even a small typo or missing number can prevent a match - cards missing a Card Name or Card Number are skipped automatically, rather than wasting a lookup on a search that's unlikely to be accurate.`,
  },

  {
    id: "shows-pre-show-summary",
    category: "Shows",
    categoryIcon: "🎪",
    title: "Pre-Show Summary Report",
    keywords: ["pre-show", "pre show", "summary", "report", "pricing check", "needs review", "before show"],
    content: `The **Pre-Show Summary** helps you make pricing decisions before you head to a show, by flagging cards worth a second look.

## Running the Report
- **Automatically:** the day before an upcoming show, VendorStax shows this report once, the first time you open the app that day.
- **Manually:** open any show with cards reserved or active, and tap **🔍 Pre-Show Check** in the show detail header. You can run this any time, as many times as you want.

## What It Checks
For every card currently attached to that show, the report flags:
- **High-value cards** - anything priced over $100, since these deserve a closer look before a show
- **Reference price not updated in 7+ days** - the card's Reference Price data is stale or was never fetched
- **No List Price set** - the card has nothing entered for List Price
- **List Price is below cost** - you'd lose money selling at the current price

## Reading the Results
Each category that has cards shows a count and a **View cards** link to expand the full list. Tap a category again to collapse it.

If nothing is flagged, you'll see a green **"Cards have passed pre-show check!"** message - you're good to go.`,
  },

  {
    id: "settings-team-management",
    category: "Settings",
    categoryIcon: "⚙️",
    title: "Team Management - Adding Staff and Admins",
    keywords: ["team", "staff", "admin", "multi-tenancy", "multiple users", "share account", "card show table", "invite", "roles"],
    content: `VendorStax lets a **Pro** account add other people to work inside the same account at the same time. This is built for the real situation at a card show: two or more people sharing one table, each on their own phone, all seeing and updating the same inventory in real time.

## Who Can Do This

Adding team members is a **Pro plan** feature. If you're on Free, you'll see an upgrade prompt instead of the Team Members section.

Your Pro plan includes you plus 1 additional team member at no extra cost. Need more people than that? Additional team members are $3 per month each, billed through PayPal. When you add someone beyond your free slot, you'll see a popup where you can choose how many additional people you need (not just one at a time) and pay for all of them in a single PayPal approval.

If you later remove a team member, that slot opens back up, you won't be charged again to add someone new into it. Changing how many paid seats you have (adding more, or reducing) replaces your existing seat subscription with a new one at the updated amount, it does not stack multiple charges.

## Where to Find It

Go to **Settings**, and look for the **Team Members** section. Each person you add logs in with their own email and their own Google account. One email can only ever belong to one VendorStax account at a time.

## Two Roles

**Admin** - can do everything the account owner can do: edit Settings, run reports, manage the CRM, add or remove other team members, and see financial details.

**Staff** - a more limited role, meant for someone helping out at a show without needing full access:
- No access to the Dashboard
- No access to the CRM
- Settings is limited to viewing your Shop and Kiosk URLs only, no PIN, no business rules, no team management
- No access to profit or cost reports (P&L, Inventory Health, Best Sellers, Lot ROI, Buy Rate, Graded Cards, Consignment, Market Intel). Kiosk Analytics is still visible
- Cannot bulk-delete cards
- Can only delete cards they personally added, and only within 24 hours

## How to Add Someone

1. In **Settings**, find the **Team Members** section
2. Enter their email address
3. Choose **Admin** or **Staff**
4. Tap **Add**

They'll be able to sign in immediately using that email. You can change anyone's role, or remove them from your team, at any time from that same screen.

## Why This Is Useful at a Show

If you're sharing a table with someone, both of you can be in Show Mode on your own device at the same time. When one of you sells or trades a card, the other sees it disappear from available inventory right away. No more double-selling the same card, and no need to reconcile two separate notebooks at the end of the day.`,
  },

  {
    id: "settings-price-setting",
    category: "Settings",
    categoryIcon: "⚙️",
    title: "Price Setting (Dynamic Pricing)",
    keywords: ["price setting", "dynamic pricing", "auto price", "automatic pricing", "pkmnprices", "markup", "admin"],
    content: `**Price Setting** automatically sets your List Price from Reference Price, so you don't have to manually reprice your whole inventory. This is a separate add-on, **$9 a month**, on top of Pro, not something Pro includes automatically. If you're on Pro but haven't added it yet, you'll see a prompt to subscribe right where the settings normally are.

## Where to Find It
**Settings → Price Setting**, below the Reference Price section.

## Turning It On
Tap the toggle. You'll see a popup explaining exactly what dynamic pricing does before it takes effect - nothing changes until you confirm.

## The Rules
Pick exactly one rule for how List Price gets calculated. Turning one on turns the others off.
- **Top of the Price Range** - List Price is set to the top of the Price Range
- **Average of the Price Range** - List Price is set to the average of the Price Range
- **Top of the Price Range plus a percentage** - your choice of percentage, added to the top of the range
- **Top of the Price Range plus a flat dollar amount** - your choice of dollar amount, added to the top of the range
- **Below the top of the Price Range by a percentage** - your choice of percentage, subtracted from the top of the range
- **Below the top of the Price Range by a flat dollar amount** - your choice of dollar amount, subtracted from the top of the range
- **Always** - your List Price will never be set below your cost plus your markup margin (from Business Settings), no matter which rule above is selected

## Locking a Card's Price
If Price Setting keeps changing a specific card's price to something you don't want, check **Price Override** on that card (shown right under List Price whenever Price Setting is turned on). A card with this checked is frozen, on-add pricing and the nightly job both leave it alone completely, no matter what its Reference Price does. Uncheck it anytime to let Price Setting manage that card again.

## Setting Your Manual Review Ceiling
The ceiling defaults to $100, but you can raise or lower it in Settings under Price Setting. Anything above your ceiling gets flagged for manual pricing instead of being auto-set, no matter which rule is selected above, the ceiling always overrides the rule. This is meant as a safety net for higher-value cards, and you're the one who decides where that line sits.

## Important: This Overwrites Existing Prices Too
Price Setting isn't just for cards without a price yet. If a card already has a List Price and its Reference Price refreshes, Price Setting recalculates and replaces it, the same as if it had no price at all. If you've manually set a price on a card for a specific reason, know that it can get overwritten the next time that card's Reference Price updates, as long as it's under your ceiling and Price Setting is on.

## When Prices Update
There's no manual refresh step to run - Price Setting applies automatically any time a Reference Price is fetched, whether that's the moment a card is added, from the nightly background pricing job, or when you tap 🔄 on an individual card. It only ever touches **In Stock** cards - both in your backoffice and your online shop. Cards reserved for an active show are left completely alone; if you remove a card from a show and return it to inventory, it becomes eligible for auto-pricing again the next time its Reference Price updates.

## Changing Your Markup Percentage
Edit the % field next to the $20–$100 rule and tap **Save %** - no need to toggle the feature off and on again. The new percentage applies the next time each card's Reference Price updates, not retroactively to prices already set.

## We Still Recommend Reviewing
Dynamic pricing is a starting point, not a substitute for judgment. Check in on your prices periodically, especially anything flagged for manual review.`,
  },

  {
    id: "settings-tax-tracking",
    category: "Settings",
    categoryIcon: "⚙️",
    title: "Tax Tracking",
    keywords: ["tax", "sales tax", "tax rate", "tax tracking", "collect tax"],
    content: `**Tax Tracking** adds sales tax on top of your sale price whenever a card sells, so you have a record of what you collected. This is a **Pro feature**.

## Where to Find It
**Settings → General → Tax Tracking**.

## Turning It On
Toggle it on and set your **Default Tax Rate**, 6.25% to start, but change it to whatever rate applies where you sell.

## How Tax Gets Calculated
Tax is added on top of your List Price at the moment a sale completes. It's never backed out of the price you already set. The rate used gets locked in on that specific sale, so if you change your rate later, past sales keep whatever rate was true at the time, they don't change retroactively.

## Where Tax Applies
Tax Tracking works across three places:
- **Manual Sales**, marking a card Sold directly from Inventory
- **Show Mode**, both single sales and Multi Sale
- **Store Mode**, completing a Store Request

Tax does **not** apply to Trades, since no cash changes hands, and there's no tax to collect on the Online Shop yet, since it doesn't have a checkout.

## Seeing What You've Collected
Your **P&L report** shows a separate Tax Collected line. It's kept apart from Revenue and Profit on purpose, tax you collect isn't your money, it's being held for the tax authority, so it shouldn't make your profit look bigger than it really is.

For a full breakdown, use the **Sales & Tax Summary** report under Reports. It groups every taxed sale by day and by where it happened (a specific show, your Store, or a Manual Sale), showing taxable sales, the rate used, tax collected, and the total for each.`,
  },

  {
    id: "inv-lots",
    category: "Inventory",
    categoryIcon: "📦",
    title: "Lots - Buying Groups of Cards Together",
    keywords: ["lot", "bulk", "group", "binder buy", "bulk buy", "allocation", "cost per card"],
    content: `A **Lot** is when you buy a bunch of cards together as one purchase (e.g. binder buy, a bulk lot, or a collection).

Instead of figuring out the cost of each card separately, you tell VendorStax the total you paid and let it calculate the cost per card.


## Creating a Lot
1. Go to **Lots** in the sidebar
2. Tap **+ New Lot**
3. Give it a name (e.g. "Spring 2026 Binder Buy")
4. Enter how much you paid in total
5. Choose how to split the cost:
   - **Even Split** - Every card gets the same cost (total ÷ number of cards)
   - **Proportional** - Cards with higher market value get assigned a higher cost

## Connecting Cards to a Lot
When you add or edit a card, you can choose which Lot it came from. The acquisition cost will fill in automatically based on the lot's settings.

**Tip:** When you pick a lot for a card, the lot's purchase date also fills in the card's acquisition date automatically. You don't have to type it!`,
  },

  // ── SHOW MODE ──────────────────────────────────────────────────────────────
  {
    id: "show-overview",
    category: "Show Mode",
    categoryIcon: "🎪",
    title: "What is Show Mode?",
    keywords: ["show mode", "show", "event", "table", "sell", "buy", "trade"],
    content: `**Show Mode** is a special part of VendorStax built for when you're at a card show or event.

It's fast, simple, and works great on a phone or tablet. Everything you need is right there while you're dealing with customers.


## Three Tabs
Show Mode has three tabs:

1. **💰 Sell** - Mark a card as sold when a customer buys it. Enter the price, choose how they paid, and you're done.
2. **🔄 Trade** (Pro only) - When a customer wants to trade their cards for yours. The app helps you figure out if the trade is fair.
3. **🛒 Buy** - Log cards you're buying from customers at the show.

## Starting Show Mode
You need to have an **active show** first. Go to **Shows**, create a show, and add cards to it. Then tap the **"Enter Show Mode"** button at the bottom of the screen.

## Always Active Show
As long as a show is active, the Enter Show Mode button stays visible on every screen. You can hop in and out of Show Mode as needed.`,
  },

  {
    id: "show-sell",
    category: "Show Mode",
    categoryIcon: "🎪",
    title: "Selling Cards in Show Mode",
    keywords: ["sell", "sale", "sold", "cash", "payment", "payment method", "venmo", "cash app", "zelle", "paypal", "bulk discount", "discount", "channel", "record"],
    content: `The **Sell tab** is how you record a card sale at your table.

## How to Sell a Card
1. Search for the card by name, or scroll through your show's card list
2. Tap the card
3. Enter the **sale price** (it pre-fills with your list price, but you can change it)
4. If you're selling more than one at a discount, pick a **bulk order discount** percentage. It's applied to the sale price when you confirm.
5. Choose the **payment method**, Cash, Venmo, Cash App, Zelle, or PayPal
6. Tap **Confirm Sale**

Every sale made through Show Mode is automatically tagged to that show, there's no separate channel to pick.

## Retroactive Sales
Did you forget to record a sale during the show? No problem. Go to **Inventory**, find the card, and mark it as sold. You can pick any past show from the channel dropdown, even ones that are already finished.

## Attaching a Sale to a Buyer (Pro)
If you know who bought the card, you can attach the sale to a buyer in your Buyers List. This helps you track their purchase history over time.

## Payment Methods in Reports
Every sale's payment method is tracked and rolled up in **Reports → Payment Methods**, right next to the existing Channels breakdown, so you can see how your customers actually pay you over time.`,
  },

  {
    id: "show-trade",
    category: "Show Mode",
    categoryIcon: "🎪",
    title: "Trading Cards in Show Mode (Pro)",
    keywords: ["trade", "trade in", "trade credit", "coming in", "going out", "swap"],
    content: `The **Trade tab** helps you evaluate trades at your table.


## How a Trade Works
A trade has two sides:
- **Cards Going Out** - Your cards the customer wants
- **Cards Coming In** - Their cards you're getting

## Step by Step
1. In **Cards Going Out**, search for the cards from your show inventory that the customer wants
2. In **Cards Coming In**, scan or search for the cards the customer is offering you
3. The app calculates the **trade credit** for cards coming in (based on your trade credit rate)
4. If the trade credit covers the cards going out, it's an even trade
5. If there's a gap, the customer pays the difference

## Scanning Cards Coming In
Tap the **📷 camera icon** next to the Cards Coming In search box. Point your camera at the card. The app will recognize it and fill in the details - including an estimated market value.

## Trade Credit Rate
Your trade credit rate (set in Settings) determines how much you offer for incoming cards. For example, at 80%, a $10 card earns $8 in trade credit.`,
  },

  {
    id: "show-buy",
    category: "Show Mode",
    categoryIcon: "🎪",
    title: "Buying Cards at a Show",
    keywords: ["buy", "buying", "purchase", "cart", "amount paid", "buy tab"],
    content: `The **Buy tab** is for when you're buying cards from people at the show.


## Adding a Card to Your Cart
1. Choose **Single Card** or **Sealed Product**
2. Tap the **📷 camera icon** to scan the card, or type the card name
3. The app will fill in the card details
4. Enter how much you're **paying** for it
5. Tap **Add to Cart**

Keep adding cards until you're done.

## Checking Out
When you're done buying, tap **Complete Purchase**. All the cards go into your inventory automatically, with the price you paid saved as the acquisition cost.

## Sealed Products (Booster Boxes, ETBs, etc.)
Switch to **Sealed Product** when buying booster boxes, Elite Trainer Boxes, or other sealed items. Enter the product name, the set, and how many units. If you're buying more than 1, the app splits the total cost across all units.

## Tips
- The **cash buy rate** from your Settings is shown as a guide. You don't have to follow it exactly, rather it's just a reminder of your target.
- You can attach the purchase session to a **Lot** to track which batch of cards these came from.`,
  },

  // ── REPORTS ────────────────────────────────────────────────────────────────
  {
    id: "rep-overview",
    category: "Reports",
    categoryIcon: "📈",
    title: "Understanding Your Reports",
    keywords: ["reports", "analytics", "data", "summary", "earnings", "profit"],
    content: `The **Reports** section gives you a bird's eye view of how your business is doing.

There are several report tabs:

| Report | What it shows |
|--------|--------------|
| **Sales Summary** | Total revenue, profit, and number of cards sold |
| **Sales by Price Range** | Which price tiers actually sell, and how well they sell through vs. what's on hand |
| **Sales & Tax Summary** | Taxed sales grouped by day and channel, only shows up once Tax Tracking is on |
| **Inventory Health** | Which cards have been sitting too long, and underpriced opportunities |
| **Lot ROI** | How much money each buying lot has made you |
| **Buy Rate Analysis** | Whether you're buying cards at the right prices |
| **Market Intel** | Deep price trends and card ratings (Pro) |
| **Kiosk Analytics** | What customers are searching for on your kiosk (Pro) |


**Tip:** Sales Summary is available on both Free and Pro plans. The other reports require Pro.`,
  },

  {
    id: "rep-market-intel",
    category: "Reports",
    categoryIcon: "📈",
    title: "Market Intelligence - Deep Dive",
    keywords: ["market intel", "market intelligence", "rank", "score", "trend", "buy", "avoid", "hold", "tier", "stability", "momentum"],
    content: `**Market Intel** is one of the most powerful tools in VendorStax. It reads monthly sales rank data and tells you which cards are hot, which are cooling down, and which ones are smart buys right now.

## Where the Data Comes From
This data is updated by the VendorStax team on behalf of every Pro member, once a month. There's nothing to upload yourself, no action needed on your end, it just shows up. If you ever see "No data yet," check back soon rather than looking for an upload button, since there isn't one anymore.

## Two Different Scores, Not One
Market Intel actually shows two separate things about each card, worth not mixing up:

**The 5-tier Recommendation** (🔥 Aggressive Buy, ✅ Strong Inventory, 📦 Stable Inventory, 👀 Watchlist, ⛔ Avoid) is based on the overall 0-100 score.

**The BUY / HOLD / AVOID verdict** shown on each card, and what the "Avoid List" tab filters by specifically, weighs a broader set of signals together (score, market phase, and for well-known Pokémon, long-term collector demand), not just the raw score alone.

A card can occasionally show a strong recommendation tier while its verdict differs, since the verdict looks at more than the score by itself.

## Understanding the Verdict
Every card gets one of three verdicts:

### ✅ BUY
This card looks like a good deal right now. It might be ranked high and still rising, priced lower than usual, or a well-known card that's historically bounced back from dips like this one.

### ⚠️ HOLD
This card is stable. Nothing exciting is happening with it, but nothing bad either. Hold your current cards; don't rush to buy more or sell.

### 🚫 AVOID
This card is losing popularity or dropping in price with no sign of slowing. It's probably not a great time to buy. If you already own it, check if you should lower your price to sell sooner.

## Not Sure What Something Means?
Tap **ℹ️ How Scoring Works** at the top of Market Intel for a plain-language breakdown of every tier, every sub-score, and every market phase, right there whenever you need it.

## The Score (0-100)
Each card gets a **score** that combines three things:
- **Momentum** - Is the rank going up or down compared to last month?
- **Stability** - Has the rank been consistent over many months?
- **Divergence** - Is there a mismatch between price and rank? (Can signal a buying opportunity)

Higher scores = better opportunity.

## The Rank
Cards are ranked by sales popularity. **Rank #1** means it's the most popular card in the data. If a card's rank went from #50 to #30, that means it's getting more popular (rank numbers go down as popularity goes up).

## Tier Badges
Pokémon cards get a **Tier badge**:
- **⭐ Tier 1** - Big names like Charizard, Pikachu, Mewtwo, Gengar. These cards have long-term collector demand. Price dips on Tier 1 cards that have already stabilized or started recovering often bounce back, but a Tier 1 card still actively declining is treated like any other card in decline, being a well-known name doesn't excuse it from a real, ongoing drop.
- **🔶 Tier 2** - Solid cards with consistent demand but less legendary status.
- No badge - Newer or less established cards.

## Market Phase
The app identifies what **phase** a card is in:

| Phase | What it means |
|-------|---------------|
| 📈 Rising | Rank and/or price are actively climbing |
| 📉 Post-Release Dip | Normal drop shortly after a new set releases, expected, not a red flag |
| 📊 Floor Forming | Still declining, but slowing down, an early sign it may be bottoming out |
| 🔄 Recovering | Price has turned back up from a recent low |
| ⚠️ In Freefall | Declining for several months straight with no sign of slowing, the riskiest phase |
| ➡️ Mature / Stable | Little recent movement either direction |

## Avoid, in Show Mode Too
If you're on Pro, the Buy tab in Show Mode checks any card you add to your cart against this same data. If it's flagged Avoid, you'll get a quick heads-up right there, no need to check Market Intel separately mid-show. This never blocks you from buying, it's just a heads-up.

## Month-by-Month History
Tap any card to see its full history, rank, and price for every month of data available. This shows you the trend at a glance.

## How Many Months of Data Are Needed?
- **1 month** - Current rankings and prices, but no trends
- **2 months** - Basic momentum (is it going up or down?)
- **3+ months** - Stability scores and full trend analysis kick in
- **5+ months** - The most accurate verdicts and scoring`,
  },

  {
    id: "rep-pnl",
    category: "Reports",
    categoryIcon: "📈",
    title: "P&L Report - Profit & Loss",
    keywords: ["p&l", "profit", "loss", "tax", "revenue", "cogs", "cost of goods", "margin", "export", "csv", "accountant", "quarterly", "annual"],
    content: `The **P&L Report** (Profit & Loss) gives you a clear picture of how your card business is performing financially. It's designed to be simple enough to understand at a glance, but detailed enough to hand to your accountant at tax time.

## How to Access It
Go to **Reports → P&L**. It's the first tab.

## Choosing a Time Period
At the top, pick the period you want to look at:
- **Year to Date** - from January 1st to today
- **Q1, Q2, Q3, Q4** - each quarter of the current year
- **All Time** - every sale ever recorded
- **Custom** - pick any start and end date you want

## The Key Numbers

**Total Revenue** - the total amount you received from all card sales in the period.

**Cost of Goods (COGS)** - the total amount you originally paid to acquire those cards.

**Gross Profit** - Revenue minus COGS. This is what you actually made. The percentage shown next to it is your profit margin.

**Cards Sold at a Loss** - cards you sold for less than you paid. Happens to everyone. The total loss amount is shown so you can account for it.

## Monthly Trend Chart
If you have sales across multiple months, a bar chart shows your revenue and cost of goods side by side each month. This helps you spot your busiest and most profitable months.

## Sales by Channel
Shows a breakdown of your sales by where they happened - Show, eBay, local sale, trade, etc. Useful for knowing which channels make you the most money.

## Sales by Show
If you record which show a sale happened at, this section breaks down revenue and profit per show. Great for deciding which shows are worth your time.

## Current Inventory Snapshot
This section shows what your unsold cards are worth right now:
- **Cost Basis** - what you paid for all unsold cards combined
- **Est. Market Value** - based on market prices or list prices
- **Unrealized Gain/Loss** - the difference between what you paid and what it's worth now

Note: Unrealized losses aren't tax deductible until you actually sell the card. But it's useful to know where you stand.

## Sales Detail Table
Tap **Show All** to see a line-by-line table of every card sold in the period, including:
- Date sold
- Card name, set, and condition
- Game type and sales channel
- Sale price, cost, and profit per card

You can sort by newest, oldest, most profit, or highest sale price.

## Exporting for Your Accountant
Tap **⬇ Export CSV** to download a spreadsheet of all your sales. This file can be opened in Excel or Google Sheets and contains every column your accountant needs to calculate your taxes.

## What About Expenses?
Show expenses (like table fees) are tracked separately in Show Mode. Subtract your total expenses from your Gross Profit to get your true net profit for tax purposes.`,
  },
  {
    id: "rep-inventory-health",
    category: "Reports",
    categoryIcon: "📈",
    title: "Inventory Health Report",
    keywords: ["inventory health", "non-mover", "aged", "underpriced", "slow seller", "stale"],
    content: `The **Inventory Health** report helps you find cards that need attention.


## Non-Movers
Cards that have been in your inventory for a long time without selling are called **non-movers**. The report breaks them into buckets:
- **30–60 days** - Getting older
- **60–90 days** - Time to think about lowering the price
- **90+ days** - These cards are costing you money by just sitting there

**What to do:** Consider lowering the price, bundling them in a lot, or trading them away.

## Underpriced Opportunities
These are cards where the **market price is 20% or more above your list price**. That means you might be selling too cheap!

Tap **"View All"** to see every underpriced card and decide if you want to raise your prices.

**Example:** If a card's market price is $50 but your list price is $35, you might be leaving money on the table.

## Tips
- Check this report once a month to stay on top of slow-moving inventory
- Raising prices on underpriced cards can add up to real money over time`,
  },

  {
    id: "rep-sales-by-price-range",
    category: "Reports",
    categoryIcon: "📈",
    title: "Sales by Price Range",
    keywords: ["sales by price range", "price bucket", "sell-through", "liquidity", "price tier"],
    content: `The **Sales by Price Range** report groups your sold cards into price buckets, from $5 & Under up to $5,000+, so you can see which price tiers actually move.

## The Buckets
Every sold card is placed into one bucket based on the price it actually sold for. The pie chart and table show how many cards sold in each bucket, what percent of your total sales that represents, and the combined dollar total for that bucket.

## Sell-Through
This is the most useful column on the report. Raw sales count alone can be misleading, a bucket that sold more cards isn't necessarily your best-performing price tier if you also have a lot more inventory sitting in it.

**Sell-through %** answers a different question: out of everything you've ever had in that price range, sold and still on hand, how much has actually sold?

**Example:** Say you've had 100 cards priced $5-$20, and sold 45 of them. That's a 45% sell-through. Meanwhile, if you've had 30 cards priced $20-$50 and sold 25, that's an 83% sell-through, even though fewer cards sold in raw numbers. The $20-$50 range is clearing out much faster relative to what you're holding, that's liquidity, not just volume.

Currently unsold cards are counted using their **List Price** to determine which bucket they belong to.

## Custom Range
Below the bucket table, you can enter your own min and max price to see sales for any specific range you're curious about, not just the preset buckets.

## Tips
- A high sell-through bucket with a lower dollar total can still be a better place to focus than a low sell-through bucket with a higher total, it tells you where your inventory is actually moving, not just where the money is
- If a price range has a low sell-through, consider whether those cards are priced right, or whether that price tier just isn't in demand from your customers`,
  },

  // ── KIOSK ──────────────────────────────────────────────────────────────────
  {
    id: "kiosk-overview",
    category: "Kiosk Mode",
    categoryIcon: "🖥",
    title: "What is Show Mode's Kiosk?",
    keywords: ["kiosk", "customer", "display", "tablet", "browse", "pin", "show mode kiosk"],
    content: `VendorStax now has two separate kiosk screens for two different situations. This article covers the one built for **shows**, tied to whichever show you have marked Active. If you run a physical shop and want a kiosk that's always on, even when no show is running, see the **Store Mode** article instead.

**Show Mode's Kiosk** is a public-facing screen you can set up at your show table. Customers can browse your available cards for that show on a tablet or display without touching your main app.


It's completely separate from your account and customers can only see your inventory, not your costs or settings.

## What Customers Can Do
- Browse all your cards for the current show
- Filter by game (Pokémon or Lorcana)
- Search by card name
- See the card image, condition, and price
- Tap a card to see full details
- Submit a card request if they don't see what they want

## Setting Up Your Kiosk
1. Go to **Settings → Kiosk Mode**
2. Copy your **Kiosk URL**, this is your unique link
3. Set a **5-digit PIN** to protect your kiosk
4. Open the link on a tablet or second device and enter your PIN


## Your Kiosk URL
Your kiosk URL is based on your business name. For example:
\`https://app.vendorstax.com/kiosk.html?v=parkertradingco\`

This URL is unique to you. Anyone who visits it needs your PIN to get in.

## The PIN
Your PIN protects your kiosk. Without it, no one can view your inventory. You set it in Settings and can reset it anytime. The same PIN also protects your Store Mode kiosk, if you use one, it's one PIN for both.`,
  },

  {
    id: "store-mode-overview",
    category: "Kiosk Mode",
    categoryIcon: "🏪",
    title: "Store Mode - An Always-On Kiosk for Your Storefront",
    keywords: ["store mode", "always on", "storefront", "shop", "card shop", "kiosk orders", "add to stack", "store requests"],
    content: `**Store Mode** is a second, separate kiosk built for shops with a physical storefront. Unlike Show Mode's kiosk, it doesn't need an Active show at all, it just shows every card you currently have In Stock, all the time.

This is a **Pro feature**, turned on in **Settings → For Card Shops**.

## Turning It On
1. Go to **Settings → For Card Shops**
2. Toggle **Store Mode** on
3. Copy your **Store URL** and open it on a tablet or display at your shop

Your Store URL uses the same PIN as your Show Mode kiosk, so there's nothing new to set up if you already use one.

## Customers Can Build a Stack
Instead of just browsing, customers using your Store kiosk can:
- Search your full in-stock inventory
- Tap **Add to Stack** on any card or sealed product they want
- Review everything in their Stack before submitting
- Enter their first and last name
- Get an order number back

No payment happens at the kiosk. This is a "please pull these for me" request, not a checkout, the sale still happens with you, in person, the normal way.

## Working a Request (Store Requests)
New orders show up under **CRM → Store Requests**, with a red count badge letting you know one's waiting. Open one to see:
- The customer's name (and you can add their email or phone if you get it)
- Each card they asked for, along with whether it's still In-Stock or already Sold through something else

For each card you have two buttons:
- **Fulfill**, marks it Reserved and turns the button green, this just tells your staff "I've pulled this one"
- **Remove**, drops that card from the order if you can't honor it

When you're done pulling everything, **Complete Order** marks every remaining card Sold and automatically adds the customer to your Buyers List. **Cancel Order** releases anything you'd Fulfilled back to In Stock instead.`,
  },

  {
    id: "settings-for-card-shops",
    category: "Settings",
    categoryIcon: "⚙️",
    title: "For Card Shops - Kiosk Options for a Storefront",
    keywords: ["for card shops", "kiosk location", "display location", "hide prices", "request form", "card shop", "storefront", "store mode"],
    content: `**Settings → For Card Shops** has a small set of options built for a shop with a physical storefront running a kiosk all the time, not just at shows. This is a **Pro feature**. Everything here defaults to off, turning nothing on changes anything about how your Show Mode kiosk currently looks.

## Store Mode
Turns on a second, separate kiosk that shows everything you have In Stock, with no need for an Active show at all. See the **Store Mode** article for the full walkthrough, including how customers build a Stack and how you work those requests under CRM.

## Display Location
When turned on, each card gets a Location field (Display Case, Binder 3, whatever makes sense to your shop), both in your own app and in your kiosks. You can type a new location value the first time you use it, and it gets saved for next time. Saved values can be reviewed and removed from this same section in Settings.

## Hide Prices in Kiosk Mode
When turned on, shoppers browsing your kiosk see every card detail except List Price. Useful if you'd rather quote a price in person at the counter instead of showing it on the screen.

## "Can't Find a Card?" Request Form
This is the form shoppers can use to leave their info when a card isn't in stock. It's on by default. Turning it off removes the form, requests you already have still show up under CRM, they just won't get any new ones through your kiosk. You can also enter an email address here to get a copy of every new request sent to your inbox as it comes in.`,
  },

  // ── BUYERS LIST ────────────────────────────────────────────────────────────
  {
    id: "buyers-overview",
    category: "CRM",
    categoryIcon: "👥",
    title: "Buyers List - Your Customer CRM",
    keywords: ["buyers", "buyers list", "customer", "crm", "want list", "purchase history"],
    content: `The **Buyers List** is your customer address book. Keep track of people who buy from you regularly, what they're looking for, and what they've bought before.


## Adding a Buyer
Go to **Buyers List → + Add Buyer**. Enter their:
- Name
- Phone number or email (optional but helpful)
- Any notes about what they collect

## Want Lists
Each buyer can have a **Want List** - a list of cards they're searching for. When you get a new card that matches someone's want list, you'll know exactly who to call.

To add to a want list: open a buyer's profile, scroll to Want List, and tap **+ Add Card**.

## Attaching Sales to a Buyer
In Show Mode's Sell tab, you can attach a sale to a specific buyer. This builds their **purchase history** automatically.

## Where Buyers Come From
Buyers can be added:
- Manually in the Buyers List
- Automatically when a customer submits a card request through your Kiosk
- From the Show Mode Sell tab

This is a **Pro feature**.`,
  },

  // ── CONSIGNORS ─────────────────────────────────────────────────────────────
  {
    id: "cons-overview",
    category: "CRM",
    categoryIcon: "👥",
    title: "Consignors - Selling Cards for Others",
    keywords: ["consignor", "consignment", "selling for others", "payout", "commission"],
    content: `A **Consignor** is someone whose cards you're selling on their behalf. You hold the cards, sell them, and pay the consignor their share when they sell.


## Setting Up a Consignor
Go to **Consignors → + Add Consignor**. Enter:
- Their name and contact info
- Any notes (e.g. "Prefers cash payout")

## Marking a Card as Consignment
When adding a card to inventory, you can mark it as a **consignment card** and link it to a consignor. This keeps your inventory accurate and you know which cards are yours and which belong to someone else.

## Paying Out Consignors
VendorStax tracks which consignment cards have sold so you can calculate what you owe each consignor. The exact payout terms are between you and the consignor. VendorStax just helps you keep the records straight.

This is a **Pro feature**.`,
  },

  // ── CARD SCANNING ──────────────────────────────────────────────────────────
  {
    id: "scan-overview",
    category: "Card Scanning",
    categoryIcon: "📷",
    title: "Scanning Cards with Your Camera",
    keywords: ["scan", "camera", "scanning", "google vision", "auto", "recognize"],
    content: `VendorStax can use your phone's camera to **recognize cards automatically**. This is much faster than typing card names especially when you're buying a lot of cards at once.


## How to Scan
1. Tap the **📷 camera icon** (next to the card name field in Add Card, Buy tab, or Trade tab)
2. Your camera opens with a card-shaped guide frame
3. Hold the card steady inside the frame - flat surface and good lighting help a lot
4. Tap the **round button** at the bottom to take the picture
5. The app reads the card name and looks it up automatically
6. You'll see a grid of matching cards - tap the right one


## Tips for Good Scans
- Lay the card on a **dark, flat surface** (a black felt pad works great)
- Make sure the **card name at the top** is fully in frame and in focus
- Avoid **glare** - move away from direct light if you see reflections on the card
- **Foil cards** can be tricky - try angling the card slightly

## Where You Can Scan
- **Adding cards to inventory** - tap 📷 in the Add Card drawer
- **Show Mode - Buy tab** - tap 📷 next to the Card Name field
- **Show Mode - Trade tab** - tap 📷 in the "Cards coming in" section

## What If the Scan Doesn't Work?
If scanning fails, the app will show you what text it was able to read. This helps you figure out what went wrong. You can always tap **Try Again** or type the card name manually instead.`,
  },

  {
    id: "scan-quick-scan",
    category: "Card Scanning",
    categoryIcon: "📷",
    title: "Quick Scan - Buying Cards Fast",
    keywords: ["quick scan", "batch scan", "buy percentage", "buying", "acquisition cost", "list price", "market price"],
    content: `**Quick Scan** is built for buying a stack of cards quickly at your table - scan one after another, then set prices for the whole batch at once. Look for the **⚡ Quick Scan** button (availability depends on your plan).

## The Flow
1. Tap **⚡ Quick Scan** and start scanning cards with your camera, one after another
2. Each scanned card gets added to a running queue - you'll see the count grow as you go
3. When you're done scanning, tap **Done** to review the batch

## Setting Prices for the Batch
At the top of the review screen is a **Buy Percentage** field - this is the default percentage of Market Price you're offering to pay, applied to every new card you scan. You can change this default anytime during the session.

Tap any card in the review list to expand it and set:
- **Market Price** - what the card is worth
- **List Price** - what you'll sell it for
- **Buy %** - this card's specific buy percentage, if different from the default

As you edit, you'll see a live **Acquisition Cost** preview (Market Price × Buy %) so you know exactly what you're paying before it's added to inventory.

## Adding Cards Manually
If a card won't scan (common with non-English cards), tap **+ Add Manually**. This shortened form only asks for Card Name, Game Type, Market Price, and List Price - the goal is getting the card into inventory fast, not filling out every field on the spot. You can always go back and add Set, Condition, or Language later.

## Finishing Up
Tap **📷 Scan More** to keep adding cards to the same batch, or **+ to Inventory** to commit everything in the queue at once. If you try to close Quick Scan with cards still sitting in the queue, you'll be asked to confirm - closing without saving discards anything not yet added to inventory.`,
  },

  // ── IMPORTING ──────────────────────────────────────────────────────────────
  {
    id: "import-overview",
    category: "Import",
    categoryIcon: "⬆",
    title: "Importing from Collectr",
    keywords: ["import", "collectr", "csv", "migrate", "transfer", "move data"],
    content: `If you already have your card collection in **Collectr**, you can move it to VendorStax quickly using the Import tool.

The Import screen gives you two options: **Collectr Import** (this article) or **Custom Import**, for collections tracked anywhere else - TCGplayer, eBay, Shopify, Google Sheets, or your own spreadsheet. See the "Custom Import - Your Own File" article for that option.

## How to Import
1. In Collectr, export your collection as a **CSV file**
2. In VendorStax, go to **Import** in the sidebar
3. Choose **Collectr Import**
4. Upload the CSV file
5. Review the cards that will be imported
6. Tap **Import** to bring them in

## What Gets Imported
- Card name and set
- Condition
- Your purchase price (acquisition cost)
- Quantity

## What to Check After Importing
- Some cards may need a **list price** added because the import doesn't always include selling prices
- The name of the card isn't always clean and sometimes it adds card numbers or added details. We recommend reviewing names for accuracy
- Check that **card numbers and rarities** look right
- If a card doesn't import correctly, you can always add it manually

## Free Plan and the 100-Card Limit
Import is available on both Free and Pro. On Free, your inventory is capped at 100 cards total. If an import would push you past that, VendorStax brings in as many cards as fit and lets you know how many were skipped, rather than blocking the whole import. Upgrade to Pro in Settings to bring in the rest.`,
  },

  {
    id: "import-custom",
    category: "Import",
    categoryIcon: "⬆",
    title: "Custom Import - Your Own File",
    keywords: ["import", "custom", "csv", "template", "tcgplayer", "ebay", "shopify", "google sheets", "own file", "spreadsheet"],
    content: `If your collection lives in **TCGplayer, eBay, Shopify, Google Sheets**, or any spreadsheet that isn't Collectr, use **Custom Import** instead. You fill in our template with your own data and upload it.

## Getting the Template
On the Import screen, choose **Custom Import**, then tap the sample file to download **VendorStax_Custom_Import_Template.csv**. It already has the correct column headers plus a few example rows so you can see the expected format before you fill in your own data.

## The Columns, Explained

**Portfolio Name** - optional. A label for where this batch of cards came from (a collection name, a supplier, whatever makes sense to you). Gets added to the card's Notes field automatically.

**Category** - the game. Accepts "Pokemon" or "Lorcana." Anything else, or left blank, defaults to Pokemon.

**Set** - the set or expansion name, exactly as it appears on the card.

**Product Name** - the card's name. This is the only column that's truly required - a row with nothing here gets skipped entirely.

**Card Number** - the number printed on the card, such as "069/172."

**Rarity** - the card's rarity. If left blank, we'll use whatever's in the Variance column instead. If both are blank, it defaults to Common.

**Variance** - an alternate rarity or variant label, used as a fallback whenever Rarity is empty.

**Grade** - leave blank, or write "Ungraded," for raw cards. For graded cards, enter the grading info as text (for example "PSA 9").

**Card Condition** - condition of the card. Defaults to Near Mint if left blank.

**Acquisition Cost** - what you paid for the card. Plain numbers only, no dollar signs or commas.

**Market At Buy** - the card's market value at the time you got it. This becomes the card's "Market Value at Acquisition" inside VendorStax.

**List Price** - what you want to sell it for. Leave blank if you'd rather set prices later.

**Date Added** - the date you acquired the card, in YYYY-MM-DD format (for example "2026-07-09"). Left blank or entered incorrectly, it defaults to today's date.

**Notes** - any free text about the card. If Portfolio Name is also filled in, both get combined together.

**Acquisition Source** - must be one of: Pack Pull, Binder Buy, Bulk Lot, Individual Buy. Anything else defaults to Individual Buy automatically.

**List Online** - controls whether the card shows up on your online shop. Accepts "yes" or "no" only.

## File Requirements
- Must be a **.csv** file
- Maximum file size: **5MB**
- Maximum **5,000 rows** per file - if your collection is larger, split it into multiple files and import them one at a time
- Numbers should be plain digits, no currency symbols or thousands separators

## Nothing Is Final Until You Say So
Uploading a file never immediately adds anything to your inventory. After upload, you'll see a review screen listing every card that will be imported, with duplicates already flagged and unchecked for you. Uncheck anything you don't want, then tap **Import** only when you're ready. Nothing touches your real inventory before that final tap.

## Changed Your Mind After Importing?
Every import is tracked as a batch. On the Import screen, under **Recent Imports**, you'll see your past imports with a count and timestamp. Tap **Undo** on any of them to remove just those cards - it won't affect anything else in your inventory, and you can always re-upload and try again.

## Free Plan and the 100-Card Limit
Import is available on both Free and Pro. On Free, your inventory is capped at 100 cards total. If an import would push you past that, VendorStax brings in as many cards as fit and lets you know how many were skipped, rather than blocking the whole import. Upgrade to Pro in Settings to bring in the rest.`,
  },

  {
    id: "crm-overview",
    category: "CRM",
    categoryIcon: "👥",
    title: "CRM - Buyers, Consignors & Shop Claims",
    keywords: ["crm","buyers","consignors","claims","shop","customers","contacts","offer","rank","first come first served"],
    content: `The **CRM** tab brings all your customer relationships into one place - buyers, consignors, and anyone who has submitted a claim from your online shop.

## Shop Claims
When a customer visits your public storefront and claims a card, submits an offer, or requests one you don't have listed, it shows up here under **Shop Claims**.

Each claim shows:
- The card name, list price, and - if the buyer entered one - their **offer price**
- Customer name and contact (email or phone)
- The exact time the request came in (e.g. "Jul 8, 4:15 PM")
- Status: **pending**, **contacted**, **accepted**, **declined**, **sold**, or **expired**

Card requests (submitted via "Request a card" on the shop) appear with a 🔍 icon so you can tell them apart from direct card claims.

## Ranking - who claimed it first
If more than one person claims the same card, each claim gets a badge like **1st of 3**, **2nd of 3**, and so on, based on the exact time each request came in. Just work down the list in order - if your 1st choice falls through, move to 2nd, then 3rd.

## Filtering
- **Status chips** at the top filter by pending/contacted/accepted/declined/sold/expired, each with a live count
- The **card dropdown** narrows the list down to claims on one specific card - handy when a popular card has a stack of offers to sort through

## Creating, editing, and deleting claims
- **+ New Claim** lets you log an offer that came in outside the shop (a DM, a phone call, an in-person offer) so it's tracked and ranked alongside everything else
- **Edit** lets you correct the card, price, offer, contact info, or add notes
- **Delete** removes a claim entirely (with a confirmation first)
- Tap any status chip to update a claim's status at any time

## Accepting an offer
When you're ready to move forward with a buyer, tap **✓ Accept & Notify Buyer**. This:
1. Marks the claim as **accepted** and timestamps it
2. Automatically emails the buyer letting them know their offer was accepted, with your business name and pickup/delivery instructions
3. Logs the event so you always have a timestamped record of when it happened

Tap **View history** on any claim to see its full timeline - when it was created, every status change, and when the buyer was notified.

## New claim notifications
Whenever a new claim or offer comes in, an orange banner appears at the top of the screen so you never miss it - **including while you're in Show Mode**. Unlike the regular toast notifications, this banner stays put until you manually close it, so a busy show table won't cause you to miss a claim.

## Buyers List
A running list of customers you've sold to. Add buyers manually or they get captured automatically during Show Mode. Useful for tracking repeat customers and purchase history.

## Consignors
People whose cards you're selling on their behalf. Set your commission rate per consignor and track which cards belong to whom. This is a **Pro feature**.`,
  },

  {
    id: "shop-overview",
    category: "Online Shop",
    categoryIcon: "🌐",
    title: "Your Online Shop",
    keywords: ["shop","online","storefront","claim","public","ecommerce","website","url","offer price"],
    content: `Your online shop is a public-facing page where anyone can browse your inventory and claim cards - no payment required upfront. You follow up to close the sale your way.

## Your Shop URL
Find your shop link in **Settings → Online Shop**. It looks like:
\`app.vendorstax.com/shop.html?s=your-slug\`

Share it on social media, your website, or anywhere customers shop. Copy it with the button in Settings.

## What Customers See
- Your business name and logo
- A card grid showing all your online-listed inventory
- Search and game filter
- Card details (condition, language, rarity, price)
- A "Claim This Card" form - they enter their name, contact info, and optionally an **offer price** they're willing to pay
- A "Request a card" option for cards not currently listed

## How to List Cards Online
Open any card → check **🌐 List online** → save. That card now appears on your shop.

For bulk listing: go to **Inventory → Bulk Edit → Online Listing → List Online**. This lets you list an entire filtered set at once.

## When Someone Claims a Card
1. You receive an email notification at your account email
2. A persistent orange banner appears at the top of the app the moment the claim comes in - even while you're in Show Mode - so you never miss it
3. The claim shows up in **CRM → Shop Claims**, ranked against any other claims on the same card by submit time
4. You follow up directly - call, text, or email - or tap **Accept & Notify Buyer** to have the app email them automatically once you've decided who to sell to

No payment processing. No shipping fees. Just a direct lead.

## This is a Pro Feature
The online shop requires a Pro plan.`,
  },

  {
    id: "show-daymode",
    category: "Show Mode",
    categoryIcon: "🎪",
    title: "Day Mode in Show Mode",
    keywords: ["day mode","night mode","light","dark","theme","show mode","brightness"],
    content: `Show Mode defaults to **Night Mode** - a dark theme designed for dimly lit venues. But at outdoor shows or brightly lit spaces, Night Mode can be hard to read.

## Switching to Day Mode
Tap the **☀️ Day** button in the top-right corner of Show Mode. The entire screen switches to a light background with dark text. Tap **🌙 Night** to switch back.

Day Mode is designed for accessibility - all fonts are larger, contrast ratios are higher, and the green balance/offer values use a darker green that reads clearly on light backgrounds.

The mode resets to Night each time you open Show Mode.`,
  },

  {
    id: "show-carryforward",
    category: "Show Mode",
    categoryIcon: "🎪",
    title: "Carrying Cards Forward to the Next Show",
    keywords: ["carry forward","next show","unsold","transfer","archive","completed"],
    content: `After a show ends, you don't have to manually re-add every unsold card to your next show. The carry-forward feature does it in one tap.

## When It Appears
The carry-forward option appears in two situations:

**During an active show** - if the show's end date has passed, the overdue banner shows two options: Return to Inventory or Carry Forward.

**On a completed show** - after closing a show, the action buttons include "🎪 Carry Forward to [Next Show Name]" alongside Archive Show.

## What It Does
- Moves all unsold cards from the old show to your next **Upcoming** show
- Cards stay **Reserved** - they don't bounce back to In Stock
- The old show is marked **Archived**
- A confirmation shows exactly how many cards will be moved and where

## The Next Show Is Picked Automatically
The app finds the next Upcoming show by earliest start date. If you have no Upcoming shows yet, create one first - then the carry-forward option will appear.`,
  },

  {
    id: "inv-online",
    category: "Inventory",
    categoryIcon: "📦",
    title: "Listing Cards Online",
    keywords: ["online","shop","storefront","is online","list online","bulk edit","public"],
    content: `Cards don't appear on your public shop automatically - you choose which ones to list. This gives you full control over what customers can browse and claim.

## Listing a Single Card
Open any card → check **🌐 List online (show in public storefront)** → save. The card now appears on your shop.

## Listing Multiple Cards at Once
Go to **Inventory → Bulk Edit**:
1. Filter by set, condition, source, or date range to narrow down the cards
2. Under **Apply to**, choose **Online Listing**
3. Tap **🌐 List Online**
4. Hit **Apply** - all filtered cards get listed at once

## Filtering by Online Status
In the inventory filter bar, tap **🌐 Online** to see only cards currently listed on your shop. Useful for auditing what's visible to customers before a sale or event.

## Removing Cards from the Shop
Uncheck the **🌐 List online** box on any card and save. Or use Bulk Edit → Online Listing → Remove from Online to delist multiple cards at once.`,
  },

  {
    id: "inv-pricing-match-reliability",
    category: "Inventory",
    categoryIcon: "📦",
    title: "Why Didn't My Card Get a Price Automatically?",
    keywords: ["dynamic pricing", "not fetched", "no price", "market price", "matching", "search vs manual", "store mode", "automated pricing", "variant", "1st edition", "unlimited", "holofoil", "reverse holo"],
    content: `This is a **Pro feature**.

When you add a card, VendorStax tries to fetch its market price right away, whether you added it by searching or by typing it in yourself. Most of the time this works instantly.

**Searching for a card gives the most reliable match**, since you're confirming the exact card yourself by picking it from real results. **Typing a card in by hand relies on VendorStax guessing the right match automatically**, using the name, number, and set you entered, and for most cards, that guess is easy and accurate.

Where it can occasionally miss is on cards that share a name with other, similar versions of themselves, special rarities like an Illustration Rare or an "ex" card that was also printed elsewhere in the same set. With more than one very similar card to choose from, an automatic match has more room to pick the wrong one, or come back empty.

## If You Use Dynamic Pricing, Online Shop, or Other Automated Pricing Features
These features all depend on a card already having a confirmed price match behind the scenes. If a card was typed in by hand and never successfully matched, automated pricing can't do anything with it, there's nothing for it to update. **Adding cards through search, rather than typing them in manually, is critical to getting reliable results out of these features.** A card you personally picked from search results is matched for good the moment you add it, no guessing involved later.

## If a Card Doesn't Get a Price Right Away
- Tap the 🔄 refresh icon on the card to try again
- If it still doesn't find a price, try adding it through search instead of typing it in by hand, picking the exact card yourself avoids the guesswork entirely
- Double-check the set you selected matches what's printed on the card, close is sometimes not close enough

## Right Card, Wrong Variant
Some cards were printed in more than one real version, a 1st Edition and an Unlimited print, or a Holofoil and a Reverse Holofoil version, and those versions can sell for very different prices. When VendorStax finds more than one real variant for a card, you'll see a **Variant** dropdown appear when adding or editing it. Picking the right one matters, since the price shown is specific to that exact variant, not a blend of all of them.

If you're not sure which variant you actually have, check the card itself against the option names shown, they come directly from real sales data for that specific card, not a generic list.

This isn't something wrong with your account. It's simply harder for an automatic match to be certain about a card that looks like several similar ones.`,
  },

];

// ── CATEGORIES for TOC ───────────────────────────────────────────────────────
const CATEGORIES = [
  { id: "Getting Started", icon: "👋" },
  { id: "Inventory",       icon: "📦" },
  { id: "Show Mode",       icon: "🎪" },
  { id: "Reports",         icon: "📈" },
  { id: "Kiosk Mode",      icon: "🖥"  },
  { id: "CRM",             icon: "👥" },
  { id: "Online Shop",     icon: "🌐" },
  { id: "Card Scanning",   icon: "📷" },
  { id: "Import",          icon: "⬆"  },
];

// ── ARTICLE RENDERER ─────────────────────────────────────────────────────────
// Renders markdown-like content: **bold**, `code`, ## Heading, bullets, numbered lists, tables
function ArticleContent({ content }) {
  const { T } = useTheme();

  const renderLine = (line, i) => {
    if (line.startsWith("|")) {
      return null; // tables handled in block
    }

    // Heading
    if (line.startsWith("## ")) {
      return (
        <h3 key={i} style={{
          fontSize:15, fontWeight:700, color:T.text,
          marginTop:24, marginBottom:8, paddingBottom:4,
          borderBottom:`1px solid ${T.border}`,
        }}>
          {line.replace("## ", "")}
        </h3>
      );
    }

    if (line.startsWith("### ")) {
      return (
        <h4 key={i} style={{
          fontSize:14, fontWeight:700, color:T.text,
          marginTop:16, marginBottom:6,
        }}>
          {line.replace("### ", "")}
        </h4>
      );
    }

    // Bullet
    if (line.startsWith("- ") || line.startsWith("• ")) {
      const text = line.replace(/^[-•]\s*/, "");
      return (
        <div key={i} style={{
          display:"flex", gap:8, marginBottom:6, alignItems:"flex-start",
        }}>
          <span style={{color:T.textMuted, flexShrink:0, marginTop:1}}>•</span>
          <span style={{fontSize:14, color:T.text, lineHeight:1.65}}>
            {renderInline(text)}
          </span>
        </div>
      );
    }

    // Numbered list
    if (/^\d+\.\s/.test(line)) {
      const num = line.match(/^(\d+)\./)[1];
      const text = line.replace(/^\d+\.\s*/, "");
      return (
        <div key={i} style={{
          display:"flex", gap:10, marginBottom:6, alignItems:"flex-start",
        }}>
          <span style={{
            color:"#3C66EE", fontWeight:700, fontSize:13,
            flexShrink:0, minWidth:18, marginTop:1,
          }}>{num}.</span>
          <span style={{fontSize:14, color:T.text, lineHeight:1.65}}>
            {renderInline(text)}
          </span>
        </div>
      );
    }

    // Empty line
    if (!line.trim()) return <div key={i} style={{height:10}} />;

    // Regular paragraph
    return (
      <p key={i} style={{
        fontSize:14, color:T.text, lineHeight:1.7,
        marginBottom:8, marginTop:0,
      }}>
        {renderInline(line)}
      </p>
    );
  };

  const renderInline = (text) => {
    // Bold: **text**
    const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
    return parts.map((part, i) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={i} style={{color:T.text}}>{part.slice(2,-2)}</strong>;
      }
      if (part.startsWith("`") && part.endsWith("`")) {
        return (
          <code key={i} style={{
            fontFamily:"'DM Mono',monospace", fontSize:12,
            background:T.surface2, padding:"1px 5px", borderRadius:4,
            color:"#88bdf2",
          }}>{part.slice(1,-1)}</code>
        );
      }
      return part;
    });
  };

  // Handle tables as a block
  const renderContent = () => {
    const lines = content.split("\n");
    const result = [];
    let i = 0;

    while (i < lines.length) {
      // Detect table start
      if (lines[i].startsWith("|") && i + 1 < lines.length && lines[i+1].startsWith("|---")) {
        const headers = lines[i].split("|").filter(c => c.trim()).map(c => c.trim());
        i += 2; // skip separator row
        const rows = [];
        while (i < lines.length && lines[i].startsWith("|")) {
          rows.push(lines[i].split("|").filter(c => c.trim()).map(c => c.trim()));
          i++;
        }
        result.push(
          <div key={`table-${i}`} style={{
            overflowX:"auto", margin:"16px 0",
          }}>
            <table style={{
              width:"100%", borderCollapse:"collapse",
              fontSize:13,
            }}>
              <thead>
                <tr>
                  {headers.map((h, j) => (
                    <th key={j} style={{
                      padding:"8px 12px", textAlign:"left",
                      background:T.surface2, color:T.textMuted,
                      fontSize:11, fontWeight:700, letterSpacing:"0.05em",
                      textTransform:"uppercase",
                      border:`0.5px solid ${T.border}`,
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, ri) => (
                  <tr key={ri} style={{
                    background: ri % 2 === 0 ? "transparent" : T.surface,
                  }}>
                    {row.map((cell, ci) => (
                      <td key={ci} style={{
                        padding:"8px 12px", color:T.text,
                        border:`0.5px solid ${T.border}`,
                        lineHeight:1.5,
                      }}>{renderInline(cell)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      } else {
        result.push(renderLine(lines[i], i));
        i++;
      }
    }
    return result;
  };

  return <div>{renderContent()}</div>;
}

// ── ADMIN EDIT MODE ───────────────────────────────────────────────────────────
function AdminEditModal({ article, onSave, onClose }) {
  const { S, T } = useTheme();
  const { toast } = useNotify();
  const [content, setContent] = useState(article.content);
  const [title, setTitle]     = useState(article.title);
  const [saving, setSaving]   = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase.from("help_articles").upsert({
        id:         article.id,
        title,
        content,
        updated_at: new Date().toISOString(),
      });
      if (error) throw error;
      onSave({ ...article, title, content });
      toast("Help article saved", "success");
      onClose();
    } catch(e) {
      toast("Save failed: " + e.message, "error");
    }
    setSaving(false);
  };

  return (
    <div style={{
      position:"fixed", inset:0, zIndex:9999,
      background:"rgba(0,0,0,0.7)",
      display:"flex", alignItems:"center", justifyContent:"center",
      padding:16,
    }}>
      <div style={{
        background:T.surface, borderRadius:16,
        border:`0.5px solid ${T.border}`,
        width:"100%", maxWidth:700,
        maxHeight:"90vh", display:"flex", flexDirection:"column",
      }}>
        {/* Header */}
        <div style={{
          display:"flex", justifyContent:"space-between", alignItems:"center",
          padding:"16px 20px", borderBottom:`1px solid ${T.border}`,
          flexShrink:0,
        }}>
          <div style={{fontSize:15, fontWeight:700}}>✏️ Edit Article</div>
          <button onClick={onClose} style={{
            background:"none", border:"none", fontSize:20,
            color:T.textMuted, cursor:"pointer",
          }}>×</button>
        </div>

        {/* Form */}
        <div style={{flex:1, overflowY:"auto", padding:20}}>
          <div style={{marginBottom:14}}>
            <label style={S.label}>Article Title</label>
            <input style={{...S.input, fontSize:15}}
              value={title} onChange={e=>setTitle(e.target.value)} />
          </div>
          <div>
            <label style={S.label}>Content</label>
            <div style={{fontSize:11, color:T.textFaint, marginBottom:6}}>
              Use **bold**, `code`, ## Heading, • Bullet, 1. Numbered list,
              and [SCREENSHOT: description] for screenshot placeholders.
            </div>
            <textarea
              style={{
                ...S.input, minHeight:400, fontFamily:"'DM Mono',monospace",
                fontSize:12, lineHeight:1.6, resize:"vertical",
                whiteSpace:"pre", overflowWrap:"normal",
              }}
              value={content}
              onChange={e=>setContent(e.target.value)}
            />
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding:"14px 20px", borderTop:`1px solid ${T.border}`,
          display:"flex", gap:10, justifyContent:"flex-end", flexShrink:0,
        }}>
          <button style={{...S.btn("ghost")}} onClick={onClose}>Cancel</button>
          <button style={{...S.btn("primary")}} onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save Article"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── MAIN HELP PAGE ────────────────────────────────────────────────────────────
const BUG_LOCATIONS = [
  "Dashboard", "Inventory", "Shows / Show Mode", "CRM / Shop Claims",
  "Reports", "Settings", "Import", "Online Shop", "Kiosk", "Sign Up / Login", "Other",
];

function SupportTicketModal({ onClose }) {
  const { S, T } = useTheme();
  const { toast } = useNotify();
  const [type, setType] = useState("");
  const [location, setLocation] = useState("");
  const [description, setDescription] = useState("");
  const [subject, setSubject] = useState("");
  const [impact, setImpact] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isBugValid = !!location && description.trim().length >= 100;
  const isFeatureValid = !!subject.trim() && !!description.trim() && !!impact;
  const canSubmit = type === "bug" ? isBugValid : type === "feature" ? isFeatureValid : false;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await submitSupportTicket({
        type, location, description: description.trim(),
        subject: subject.trim(), impact,
      });
      toast("Ticket submitted, thanks for the report!", "success");
      onClose();
    } catch(e) {
      toast("Failed to submit: " + e.message, "error");
    }
    setSubmitting(false);
  };

  return (
    <div style={S.overlay} onClick={e=>{ if (e.target===e.currentTarget) onClose(); }}>
      <div style={S.drawer}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
          <div style={S.h2}>Contact Support</div>
          <button style={{...S.btn("ghost"),padding:"4px 10px"}} onClick={onClose}>✕</button>
        </div>

        <div style={{marginBottom:14}}>
          <label style={S.label}>What's this about? *</label>
          <select style={S.select} value={type} onChange={e=>setType(e.target.value)}>
            <option value="">Select...</option>
            <option value="bug">Reporting a Bug</option>
            <option value="feature">Feature Request</option>
          </select>
        </div>

        {type === "bug" && (
          <>
            <div style={{marginBottom:14}}>
              <label style={S.label}>Where in the app? *</label>
              <select style={S.select} value={location} onChange={e=>setLocation(e.target.value)}>
                <option value="">Select...</option>
                {BUG_LOCATIONS.map(l=><option key={l}>{l}</option>)}
              </select>
            </div>
            <div style={{marginBottom:6}}>
              <label style={S.label}>What happened? *</label>
              <textarea style={{...S.input,minHeight:120,resize:"vertical"}}
                value={description} onChange={e=>setDescription(e.target.value)}
                placeholder="Describe what you were doing, what you expected, and what actually happened..." />
            </div>
            <div style={{fontSize:11, marginBottom:14,
              color: description.trim().length>=100 ? "#4ade80" : T.textFaint}}>
              {description.trim().length}/100 characters minimum
            </div>
          </>
        )}

        {type === "feature" && (
          <>
            <div style={{marginBottom:14}}>
              <label style={S.label}>Subject *</label>
              <input style={S.input} value={subject} onChange={e=>setSubject(e.target.value)}
                placeholder="Short summary of the feature" />
            </div>
            <div style={{marginBottom:14}}>
              <label style={S.label}>Why is this important? *</label>
              <textarea style={{...S.input,minHeight:100,resize:"vertical"}}
                value={description} onChange={e=>setDescription(e.target.value)}
                placeholder="Tell us why this would help..." />
            </div>
            <div style={{marginBottom:16}}>
              <label style={S.label}>Impact *</label>
              <div style={{display:"flex",gap:8}}>
                {[["all","Helps all vendors"],["workflow","Helps my specific workflow"]].map(([val,lbl])=>(
                  <button key={val} onClick={()=>setImpact(val)}
                    style={{flex:1,padding:"10px",borderRadius:10,fontSize:12,fontWeight:600,cursor:"pointer",
                      border:`1px solid ${impact===val?"#3C66EE":T.border}`,
                      background:impact===val?"rgba(60,102,238,0.1)":"transparent",
                      color:impact===val?"#88bdf2":T.textMuted}}>
                    {lbl}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {type && (
          <button style={{...S.btn("primary"),width:"100%",padding:"14px"}}
            onClick={handleSubmit} disabled={!canSubmit || submitting}>
            {submitting ? "Sending…" : "Submit Ticket"}
          </button>
        )}
      </div>
    </div>
  );
}

export default function HelpPage() {
  const { S, T }          = useTheme();
  const { isAdmin }       = usePlan();
  const [search, setSearch]           = useState("");
  const [activeId, setActiveId]       = useState("gs-welcome");
  const [editingArticle, setEditing]  = useState(null);
  const [articles, setArticles]       = useState(DEFAULT_ARTICLES);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [expandedCats, setExpanded]   = useState(
    Object.fromEntries(CATEGORIES.map(c => [c.id, true]))
  );
  const contentRef = useRef(null);

  // Load any admin overrides from DB
  useEffect(() => {
    supabase.from("help_articles").select("id,title,content")
      .then(({ data }) => {
        if (!data?.length) return;
        setArticles(prev => prev.map(a => {
          const override = data.find(d => d.id === a.id);
          return override ? { ...a, ...override } : a;
        }));
      })
      .catch(() => {}); // silently fail if table doesn't exist yet
  }, []);

  // Filtered articles based on search
  const filtered = useMemo(() => {
    if (!search.trim()) return articles;
    const q = search.toLowerCase();
    return articles.filter(a =>
      (a.title||"").toLowerCase().includes(q) ||
      (a.content||"").toLowerCase().includes(q) ||
      a.keywords?.some(k => k?.toLowerCase().includes(q))
    );
  }, [search, articles]);

  const activeArticle = articles.find(a => a.id === activeId) || articles[0];

  const toggleCat = (cat) => setExpanded(e => ({...e, [cat]: !e[cat]}));

  const handleSaved = (updated) => {
    setArticles(prev => prev.map(a => a.id === updated.id ? updated : a));
  };

  const scrollTop = () => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  };

  const selectArticle = (id) => {
    setActiveId(id);
    setSearch("");
    scrollTop();
  };

  return (
    <div style={{
      ...S.page,
      display:"flex", gap:0, height:"calc(100vh - 60px)",
      padding:0, maxWidth:"none",
    }}>

      {/* ── LEFT SIDEBAR: TOC ─────────────────────────────────────────────── */}
      <div style={{
        width:240, flexShrink:0,
        borderRight:`1px solid ${T.border}`,
        overflowY:"auto", background:T.surface,
        display:"flex", flexDirection:"column",
      }}>
        {/* Header */}
        <div style={{
          padding:"16px 16px 12px",
          borderBottom:`1px solid ${T.border}`,
          flexShrink:0,
        }}>
          <div style={{display:"flex", alignItems:"center", gap:8, marginBottom:12}}>
            <img src="/help-icon.svg" alt="Help"
              style={{width:20, height:20}}
              onError={e=>e.target.style.display="none"} />
            <span style={{fontSize:15, fontWeight:700}}>Help Center</span>
          </div>
          {/* Search */}
          <input
            style={{
              ...S.input, fontSize:12, padding:"8px 12px",
              width:"100%", boxSizing:"border-box",
            }}
            placeholder="Search articles…"
            value={search}
            onChange={e=>setSearch(e.target.value)}
          />
        </div>

        {/* Search results */}
        {search.trim() ? (
          <div style={{padding:"8px 0"}}>
            {filtered.length === 0 ? (
              <div style={{padding:"12px 16px", fontSize:12, color:T.textFaint}}>
                No articles found for "{search}"
              </div>
            ) : (
              filtered.map(a => (
                <button key={a.id}
                  onClick={() => selectArticle(a.id)}
                  style={{
                    width:"100%", textAlign:"left", padding:"10px 16px",
                    background: activeId===a.id ? "rgba(60,102,238,0.1)" : "none",
                    border:"none", cursor:"pointer",
                    borderLeft: activeId===a.id ? "3px solid #3C66EE" : "3px solid transparent",
                    color:T.text, fontFamily:"'Sora',sans-serif",
                  }}>
                  <div style={{fontSize:12, fontWeight:600}}>{a.title}</div>
                  <div style={{fontSize:11, color:T.textMuted}}>{a.category}</div>
                </button>
              ))
            )}
          </div>
        ) : (
          /* Table of Contents */
          <div style={{padding:"8px 0"}}>
            {CATEGORIES.map(cat => {
              const catArticles = articles.filter(a => a.category === cat.id);
              if (!catArticles.length) return null;
              return (
                <div key={cat.id}>
                  <button
                    onClick={() => toggleCat(cat.id)}
                    style={{
                      width:"100%", textAlign:"left",
                      padding:"8px 16px", background:"none",
                      border:"none", cursor:"pointer",
                      display:"flex", alignItems:"center",
                      justifyContent:"space-between",
                      fontFamily:"'Sora',sans-serif",
                    }}>
                    <span style={{
                      fontSize:11, fontWeight:700, color:T.textMuted,
                      textTransform:"uppercase", letterSpacing:"0.06em",
                      display:"flex", alignItems:"center", gap:6,
                    }}>
                      <span>{cat.icon}</span>{cat.id}
                    </span>
                    <span style={{fontSize:10, color:T.textFaint}}>
                      {expandedCats[cat.id] ? "▲" : "▼"}
                    </span>
                  </button>
                  {expandedCats[cat.id] && catArticles.map(a => (
                    <button key={a.id}
                      onClick={() => selectArticle(a.id)}
                      style={{
                        width:"100%", textAlign:"left",
                        padding:"8px 16px 8px 28px",
                        background: activeId===a.id ? "rgba(60,102,238,0.1)" : "none",
                        border:"none", cursor:"pointer",
                        borderLeft: activeId===a.id
                          ? "3px solid #3C66EE" : "3px solid transparent",
                        color: activeId===a.id ? "#3C66EE" : T.text,
                        fontFamily:"'Sora',sans-serif",
                        fontSize:13, fontWeight: activeId===a.id ? 600 : 400,
                      }}>
                      {a.title}
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── RIGHT: ARTICLE ────────────────────────────────────────────────── */}
      <div ref={contentRef}
        style={{flex:1, overflowY:"auto", padding:"28px 36px 60px"}}>

        {/* Article header */}
        <div style={{
          display:"flex", justifyContent:"space-between",
          alignItems:"flex-start", marginBottom:6,
        }}>
          <div>
            <div style={{
              fontSize:11, fontWeight:700, color:"#3C66EE",
              textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:6,
            }}>
              {activeArticle.categoryIcon} {activeArticle.category}
            </div>
            <h1 style={{
              fontSize:22, fontWeight:700, color:T.text,
              margin:0, lineHeight:1.3,
            }}>
              {activeArticle.title}
            </h1>
          </div>
          {/* Admin edit button */}
          {isAdmin && (
            <button
              onClick={() => setEditing(activeArticle)}
              style={{
                ...S.btn("ghost"), fontSize:12, padding:"6px 14px",
                flexShrink:0, marginLeft:16,
              }}>
              ✏️ Edit
            </button>
          )}
        </div>

        <div style={{
          height:1, background:T.border, margin:"16px 0 24px",
        }} />

        {/* Article content */}
        <ArticleContent content={activeArticle.content} />

        {/* Navigation footer */}
        <div style={{
          marginTop:48, paddingTop:16,
          borderTop:`1px solid ${T.border}`,
          display:"flex", justifyContent:"space-between",
          gap:12,
        }}>
          {(() => {
            const all = articles;
            const idx = all.findIndex(a => a.id === activeId);
            const prev = idx > 0 ? all[idx-1] : null;
            const next = idx < all.length-1 ? all[idx+1] : null;
            return (
              <>
                <div>
                  {prev && (
                    <button onClick={() => selectArticle(prev.id)} style={{
                      ...S.btn("ghost"), fontSize:12, padding:"8px 14px",
                    }}>
                      ← {prev.title}
                    </button>
                  )}
                </div>
                <div>
                  {next && (
                    <button onClick={() => selectArticle(next.id)} style={{
                      ...S.btn("ghost"), fontSize:12, padding:"8px 14px",
                    }}>
                      {next.title} →
                    </button>
                  )}
                </div>
              </>
            );
          })()}
        </div>

        {/* Contact support */}
        <div style={{
          marginTop:32, padding:16, borderRadius:12,
          background:T.surface2, border:`0.5px solid ${T.border}`,
          textAlign:"center",
        }}>
          <div style={{fontSize:13, color:T.textMuted, marginBottom:8}}>
            Still have questions? We're happy to help.
          </div>
          <button onClick={()=>setShowTicketModal(true)}
            style={{
              ...S.btn("primary"), border:"none",
              display:"inline-block", fontSize:13, cursor:"pointer",
            }}>
            Contact Support →
          </button>
        </div>
      </div>

      {showTicketModal && <SupportTicketModal onClose={()=>setShowTicketModal(false)} />}

      {/* Admin edit modal */}
      {editingArticle && (
        <AdminEditModal
          article={editingArticle}
          onSave={handleSaved}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
